import React, { useState, useEffect } from 'react';
import ReviewsFilters from './ReviewsFilters';
import ReviewsTable from './ReviewsTable';
import ReviewsMobileView from './ReviewsMobileView';
import CreateReviewForm from './CreateReviewForm';
import { Plus } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const ReviewsPage = ({ onShowToast }) => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isAdminFilter, setIsAdminFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const pageSize = 50;

    const fetchReviews = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString(),
            });

            if (statusFilter !== 'all') {
                params.append('status', statusFilter);
            }
            if (isAdminFilter !== 'all') {
                params.append('is_admin', isAdminFilter === 'true' ? 'true' : 'false');
            }
            if (searchTerm) {
                params.append('search', searchTerm);
            }

            const response = await fetch(`${API_BASE_URL}/api/reviews/?${params.toString()}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setReviews(data.items || []);
            setTotal(data.total || 0);
            setTotalPages(data.total_pages || 1);
            setError(null);
        } catch (err) {
            console.error('Error fetching reviews:', err);
            setError('Не удалось загрузить отзывы');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, [page, statusFilter, isAdminFilter, searchTerm]);

    const handleModerate = async (reviewId, newStatus) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/reviews/${reviewId}/moderate?new_status=${newStatus}`, {
                method: 'PATCH',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (onShowToast) {
                onShowToast(newStatus === 'approved' ? 'Отзыв одобрен' : 'Отзыв отклонен');
            }
            fetchReviews();
        } catch (err) {
            console.error('Error moderating review:', err);
            if (onShowToast) {
                onShowToast('Ошибка при модерации отзыва', 'error');
            }
        }
    };

    const handleDelete = async (reviewId) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот отзыв?')) {
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/reviews/${reviewId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (onShowToast) {
                onShowToast('Отзыв удален');
            }
            fetchReviews();
        } catch (err) {
            console.error('Error deleting review:', err);
            if (onShowToast) {
                onShowToast('Ошибка при удалении отзыва', 'error');
            }
        }
    };

    if (loading && reviews.length === 0) {
        return (
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-2xl font-bold text-slate-800">Модерация отзывов</h2>
                </div>
                <div className="flex items-center justify-center py-12">
                    <div className="text-slate-500">Загрузка отзывов...</div>
                </div>
            </div>
        );
    }

    if (error && reviews.length === 0) {
        return (
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h2 className="text-2xl font-bold text-slate-800">Модерация отзывов</h2>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-rose-600">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <h2 className="text-2xl font-bold text-slate-800">Модерация отзывов</h2>
                <button
                    onClick={() => setShowCreateForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                    <Plus size={20} />
                    Создать отзыв от админа
                </button>
            </div>
            
            {showCreateForm && (
                <CreateReviewForm
                    onClose={() => setShowCreateForm(false)}
                    onSuccess={() => {
                        fetchReviews();
                        if (onShowToast) {
                            onShowToast('Отзыв создан');
                        }
                    }}
                />
            )}

            <ReviewsFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                isAdminFilter={isAdminFilter}
                setIsAdminFilter={setIsAdminFilter}
            />

            <ReviewsTable
                reviews={reviews}
                onModerate={handleModerate}
                onDelete={handleDelete}
                loading={loading}
            />

            <ReviewsMobileView
                reviews={reviews}
                onModerate={handleModerate}
                onDelete={handleDelete}
                loading={loading}
            />

            {/* Пагинация */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                        Назад
                    </button>
                    <span className="px-4 py-2 text-slate-600">
                        Страница {page} из {totalPages} (всего {total})
                    </span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-4 py-2 border border-slate-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                        Вперед
                    </button>
                </div>
            )}
        </div>
    );
};

export default ReviewsPage;
